/* eslint-disable jsx-a11y/anchor-is-valid */
import {ClipboardEvent, FC, FormEvent, KeyboardEvent, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {PageLink, PageTitle} from '../../../_metronic/layout/core'
import {KTSVG} from '../../../_metronic/helpers'
import { useToast } from '../components/Toats';
import { Loading } from '../components/Loadings/loading'
import { LoadingSpan } from '../components/Loadings/loadingSpan'
import listLayoutStyles from '../components/ListTableLayout/listTableLayout.module.css'
import styles from './kioskDevices.module.css'
import {
  deleteKioskDevice,
  getKioskDevices,
  KioskDevice,
  postAuthorizeKioskDevice,
} from '../../services/kioskService';

const kioskBreadcrumbs: Array<PageLink> = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const CODE_LENGTH = 6;
// A TV atualiza o "último acesso" a cada 5 min; acima de 15 min sem sinal consideramos desligada
const ONLINE_WINDOW_MS = 15 * 60 * 1000;

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString('pt-BR', {dateStyle: 'short', timeStyle: 'short'}) : '-';

const formatRelative = (value: string | null) => {
  if (!value) return 'Nunca acessou'
  const minutes = Math.floor((Date.now() - new Date(value).getTime()) / 60000)
  if (minutes < 1) return 'Agora mesmo'
  if (minutes < 60) return `Há ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `Há ${hours} h`
  const days = Math.floor(hours / 24)
  return `Há ${days} dia${days > 1 ? 's' : ''}`
}

const isOnline = (device: KioskDevice) =>
  !!device.lastSeenAt && Date.now() - new Date(device.lastSeenAt).getTime() < ONLINE_WINDOW_MS

const sanitizeCode = (value: string) => value.toUpperCase().replace(/[^A-Z]/g, '')

type CodeInputProps = {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

// Seis caixas para o código, no mesmo formato exibido na TV (aceita colar o código inteiro)
const CodeInput: FC<CodeInputProps> = ({value, onChange, disabled}) => {
  const refs = useRef<Array<HTMLInputElement | null>>([])

  const focus = (index: number) => {
    const input = refs.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))]
    if (input) input.focus()
  }

  const setCharAt = (index: number, char: string) => {
    const chars = value.padEnd(CODE_LENGTH, ' ').split('')
    chars[index] = char || ' '
    onChange(chars.join('').replace(/\s+$/, '').replace(/\s/g, ''))
  }

  const handleChange = (index: number, raw: string) => {
    const clean = sanitizeCode(raw)
    if (!clean) return
    if (clean.length > 1) {
      onChange((value.slice(0, index) + clean).slice(0, CODE_LENGTH))
      focus(index + clean.length)
      return
    }
    setCharAt(index, clean)
    focus(index + 1)
  }

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (value[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1))
      } else if (index > 0) {
        onChange(value.slice(0, index - 1) + value.slice(index))
        focus(index - 1)
      }
    } else if (event.key === 'ArrowLeft') {
      focus(index - 1)
    } else if (event.key === 'ArrowRight') {
      focus(index + 1)
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const clean = sanitizeCode(event.clipboardData.getData('text')).slice(0, CODE_LENGTH)
    if (!clean) return
    onChange(clean)
    focus(clean.length)
  }

  return (
    <div className={styles.codeGroup}>
      {Array.from({length: CODE_LENGTH}).map((_, index) => (
        <input
          key={index}
          ref={(element) => { refs.current[index] = element }}
          className={`form-control form-control-solid ${styles.codeBox} ${value[index] ? styles.codeBoxFilled : ''}`}
          value={value[index] || ''}
          maxLength={CODE_LENGTH}
          inputMode='text'
          autoComplete='off'
          autoCapitalize='characters'
          spellCheck={false}
          disabled={disabled}
          aria-label={`Letra ${index + 1} do código`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  )
}

type RevokeModalProps = {
  device: KioskDevice | null
  loading: boolean
  onConfirm: () => void
  onClose: () => void
}

const modalsRoot = document.getElementById('root-modals') || document.body

const RevokeModal: FC<RevokeModalProps> = ({device, loading, onConfirm, onClose}) =>
  createPortal(
    <Modal
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-450px'
      show={!!device}
      onHide={onClose}
      backdrop={true}
    >
      <div className='modal-body text-center px-10 pt-10 pb-8'>
        <div className='symbol symbol-60px symbol-circle mb-5'>
          <span className='symbol-label bg-light-danger'>
            <KTSVG path='/media/icons/duotune/electronics/elc004.svg' className='svg-icon-2x svg-icon-danger' />
          </span>
        </div>
        <h3 className='fw-bold text-gray-900 mb-2'>Desconectar TV?</h3>
        <div className='text-gray-600 fs-6 mb-8'>
          <span className='fw-bold text-gray-800'>{device?.name}</span> deixará de exibir as usinas
          e voltará para a tela de código.
        </div>
        <div className='d-flex justify-content-center gap-3'>
          <button className='btn btn-light' onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button className='btn btn-danger' onClick={onConfirm} disabled={loading}>
            {loading ? <LoadingSpan style={{width: '1.2rem', height: '1.2rem'}} /> : 'Desconectar'}
          </button>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )

const STEPS = [
  {title: 'Abra o link na TV', description: 'Use o navegador da Smart TV para acessar o endereço abaixo.'},
  {title: 'Anote o código', description: 'A TV exibirá um código de 6 letras, válido por 10 minutos.'},
  {title: 'Autorize aqui', description: 'Digite o código, dê um nome à TV e confirme.'},
]

const KioskDevicesPage: FC = () => {
  const { toggleToast } = useToast()
  const [devices, setDevices] = useState<KioskDevice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [code, setCode] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deviceToRevoke, setDeviceToRevoke] = useState<KioskDevice | null>(null);
  const [revoking, setRevoking] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  const kioskUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}/kiosk`;
  const onlineCount = devices.filter(isOnline).length;

  const getDevices = async () => {
    try {
      setLoading(true)
      const myDevices = await getKioskDevices()
      setDevices(Array.isArray(myDevices) ? myDevices : [])
    } catch (error) {
      console.log(error)
      toggleToast("Erro ao buscar os dispositivos!", "error")
    } finally {
      setLoading(false)
    }
  }

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(kioskUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toggleToast("Não foi possível copiar o link.", "error")
    }
  }

  const authorize = async (event: FormEvent) => {
    event.preventDefault()
    if (code.length !== CODE_LENGTH) return

    try {
      setSubmitting(true)
      await postAuthorizeKioskDevice({ code, name })
      toggleToast("TV conectada com sucesso!", "success")
      setCode('')
      setName('')
      getDevices()
    } catch (error: any) {
      toggleToast(error?.response?.data?.message || "Código inválido ou expirado!", "error")
    } finally {
      setSubmitting(false)
    }
  }

  const revoke = async () => {
    if (!deviceToRevoke) return

    try {
      setRevoking(true)
      await deleteKioskDevice(deviceToRevoke.id)
      toggleToast("Dispositivo desconectado!", "success")
      setDeviceToRevoke(null)
      getDevices()
    } catch (error) {
      console.log(error)
      toggleToast("Erro ao desconectar o dispositivo!", "error")
    } finally {
      setRevoking(false)
    }
  }

  useEffect(() => {
    getDevices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='row g-5 g-xl-8'>
      <div className='col-xl-5'>
        <div className='card card-xl-stretch mb-xl-8'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title d-flex align-items-center'>
              <div className='symbol symbol-45px me-4'>
                <span className='symbol-label bg-light-primary'>
                  <KTSVG path='/media/icons/duotune/electronics/elc004.svg' className='svg-icon-2x svg-icon-primary' />
                </span>
              </div>
              <div className='d-flex flex-column'>
                <span className='fw-bold fs-3 text-gray-900'>Conectar nova TV</span>
                <span className='text-muted fw-semibold fs-7'>Modo showroom com as usinas da empresa</span>
              </div>
            </div>
          </div>

          <div className='card-body pt-5'>
            <div className={styles.steps}>
              {STEPS.map((step, index) => (
                <div key={step.title} className={styles.step}>
                  <span className={styles.stepNumber}>{index + 1}</span>
                  <div>
                    <div className='fw-bold text-gray-800 fs-6'>{step.title}</div>
                    <div className='text-muted fs-7'>{step.description}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className={`${styles.urlBox} mb-8`}>
              <KTSVG path='/media/icons/duotune/coding/cod007.svg' className='svg-icon-3 svg-icon-primary me-3' />
              <span className={styles.urlText}>{kioskUrl}</span>
              <button type='button' className='btn btn-sm btn-light-primary ms-3' onClick={copyUrl}>
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>

            <form onSubmit={authorize}>
              <label className='form-label fw-semibold required'>Código exibido na TV</label>
              <CodeInput value={code} onChange={setCode} disabled={submitting} />

              <label className='form-label fw-semibold mt-6'>Nome do dispositivo</label>
              <input
                className='form-control form-control-solid'
                placeholder='Ex.: TV Showroom Recepção'
                value={name}
                maxLength={100}
                disabled={submitting}
                onChange={(e) => setName(e.target.value)}
              />

              <button
                type='submit'
                className='btn btn-primary w-100 mt-8'
                disabled={submitting || code.length !== CODE_LENGTH}
              >
                {submitting ? (
                  <LoadingSpan style={{width: '1.2rem', height: '1.2rem'}} />
                ) : (
                  <>
                    <KTSVG path='/media/icons/duotune/arrows/arr012.svg' className='svg-icon-3 me-2' />
                    Autorizar TV
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className='col-xl-7'>
        <div className={`card card-xl-stretch mb-xl-8 ${listLayoutStyles.listCard}`}>
          <div className='card-header border-0 pt-6'>
            <div className='card-title d-flex flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>TVs conectadas</span>
              <span className='text-muted fw-semibold fs-7 mt-1'>
                {devices.length} dispositivo(s) · {onlineCount} em exibição
              </span>
            </div>
            <div className='card-toolbar'>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light-primary'
                onClick={getDevices}
                disabled={loading}
                title='Atualizar lista'
              >
                <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3' />
              </button>
            </div>
          </div>

          <div className={`card-body py-3 ${listLayoutStyles.listTablePanel}`}>
            {loading ? (
              <div className='d-flex justify-content-center py-15'>
                <Loading myClass='spinner-border' />
              </div>
            ) : devices.length === 0 ? (
              <div className={styles.empty}>
                <div className='symbol symbol-70px symbol-circle mb-5'>
                  <span className='symbol-label bg-light'>
                    <KTSVG path='/media/icons/duotune/electronics/elc004.svg' className='svg-icon-3x svg-icon-gray-500' />
                  </span>
                </div>
                <div className='fw-bold text-gray-800 fs-5'>Nenhuma TV conectada</div>
                <div className='text-muted fs-7 mt-1'>Siga os passos ao lado para conectar a primeira.</div>
              </div>
            ) : (
              <div className={`table-responsive ${listLayoutStyles.listTableWrap}`}>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bolder text-muted fs-7 text-uppercase gs-0'>
                      <th className='min-w-200px'>Dispositivo</th>
                      <th className='min-w-100px'>Status</th>
                      <th className='min-w-125px'>Último acesso</th>
                      <th className='min-w-100px text-end'>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((device) => {
                      const online = isOnline(device)
                      return (
                        <tr key={device.id}>
                          <td>
                            <div className='d-flex align-items-center'>
                              <div className='symbol symbol-40px me-4'>
                                <span className={`symbol-label ${online ? 'bg-light-success' : 'bg-light'}`}>
                                  <KTSVG
                                    path='/media/icons/duotune/electronics/elc004.svg'
                                    className={`svg-icon-2 ${online ? 'svg-icon-success' : 'svg-icon-gray-500'}`}
                                  />
                                </span>
                              </div>
                              <div className='d-flex flex-column'>
                                <span className='text-gray-900 fw-bold fs-6'>{device.name}</span>
                                <span className='text-muted fs-7'>
                                  Conectada em {formatDate(device.authorizedAt)}
                                  {device.scope === 'FILIAL' && (
                                    <span className='badge badge-light-primary fw-semibold ms-2'>Filial</span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${online ? 'badge-light-success' : 'badge-light'} fw-bold`}>
                              <span className={`${styles.dot} ${online ? styles.dotOnline : ''}`} />
                              {online ? 'Em exibição' : 'Inativa'}
                            </span>
                          </td>
                          <td>
                            <span className='text-gray-800 fw-semibold d-block fs-7'>{formatRelative(device.lastSeenAt)}</span>
                            <span className='text-muted fs-8'>{formatDate(device.lastSeenAt)}</span>
                          </td>
                          <td className='text-end'>
                            <button
                              className='btn btn-sm btn-light-danger'
                              onClick={() => setDeviceToRevoke(device)}
                            >
                              <KTSVG path='/media/icons/duotune/arrows/arr015.svg' className='svg-icon-4 me-1' />
                              Revogar
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <RevokeModal
        device={deviceToRevoke}
        loading={revoking}
        onConfirm={revoke}
        onClose={() => !revoking && setDeviceToRevoke(null)}
      />
    </div>
  )
}

const KioskDevicesWrapper: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={kioskBreadcrumbs}>Dispositivos TV</PageTitle>
      <KioskDevicesPage />
    </>
  )
}

export {KioskDevicesWrapper}
